/* eslint-disable arrow-body-style */
import {
  PC_CONSOLE_COMBINE_ENTITY_TYPE,
  PC_CONSOLE_COMBINE_SEPARATE_ENTITY_TYPES,
} from '@intelligence/pages/TopCharts/_config/topChartsConst';

import type { IGameType } from '@intelligence/types/common';
import type { ITrendingGame, ITrendingGameEntityTypeData } from '../../../types';
import type { IEntityTypeSegment } from '../types';

/**
 * 特殊处理pc、console 为 combine 聚合
 * @param entityType 游戏entity_type
 * @returns mobile、combine
 */
export const getCombileEntityType = (entityType: IGameType): IGameType | typeof PC_CONSOLE_COMBINE_ENTITY_TYPE => {
  return PC_CONSOLE_COMBINE_SEPARATE_ENTITY_TYPES.includes(entityType) ? PC_CONSOLE_COMBINE_ENTITY_TYPE : entityType;
};

/**
 * 判断当前选项的entity_type 是否 与当前选中entity_type 匹配
 * @param optionEntityType 当前选项的entity_type
 * @param entityType 当前选中的entity_type
 * @returns 当前选项的entity_type 是否 与当前选中entity_type 匹配
 */
const isOptionEntityTypeMatched = (optionEntityType: IGameType, entityType: IEntityTypeSegment): boolean => {
  if (entityType === 'all') return true;
  if (entityType === 'mobile') return optionEntityType === 'mobile';
  if (entityType === PC_CONSOLE_COMBINE_ENTITY_TYPE)
    return PC_CONSOLE_COMBINE_SEPARATE_ENTITY_TYPES.includes(optionEntityType);
  return true;
};

/**
 * 判断当前选项 跟 当前搜索输入的关键字是否匹配
 * @param name 当前选项name
 * @param chars 当前搜索输入的关键字 split后的 char[]
 * @returns 当前选项 跟 当前搜索输入的关键字是否匹配
 */
const isOptionNameMatched = (name: string, chars: string[]): boolean => {
  if (!name) return true;
  return chars?.every((char) => name?.toLowerCase()?.includes(char));
};

/**
 * 根据选中的entityType, keyword过滤对应的游戏列表数据
 * @param options 游戏列表数据
 * @param entityType 当前选中的entityType，all、mobile、combine
 * @param keyword 当前搜索输入的关键字
 * @returns 根据选中的entityType，keyword过滤后的游戏列表数据
 */
export const getOptionsByEntityTypeAndKeyword = (
  options: ITrendingGame[],
  cfg: {
    entityType: IEntityTypeSegment;
    keyword: string;
  },
): ITrendingGame[] => {
  if (!Array.isArray(options)) return [];
  const { entityType, keyword } = cfg;
  const keywordChars = keyword?.trim()?.toLowerCase()?.split('') || [];
  // 是否需要检查name 是否 跟 搜索输入关键字匹配
  const isNeedCheckOptionNameMatched = keywordChars?.length > 0;
  return options.filter((option) => {
    const isOptionNameMatchedFlag = !isNeedCheckOptionNameMatched || isOptionNameMatched(option?.name, keywordChars);
    return isOptionEntityTypeMatched(option?.type, entityType) && isOptionNameMatchedFlag;
  });
};

const CASCADER_OPTIONS_SORT_KEYS = ['mobile', PC_CONSOLE_COMBINE_ENTITY_TYPE];
const cascaderOptionsSorter = <T extends ITrendingGameEntityTypeData>(a: T, b: T): number => {
  const aIndex = CASCADER_OPTIONS_SORT_KEYS.indexOf(a?.type);
  const bIndex = CASCADER_OPTIONS_SORT_KEYS.indexOf(b?.type);
  if (aIndex === -1 && bIndex === -1) return 0;
  if (aIndex === -1) return 1;
  if (bIndex === -1) return -1;
  return aIndex - bIndex;
};
const optionsSorter = <T extends ITrendingGame>(a: T, b: T, keyword: string): number => {
  const aName = a?.name || '';
  const bName = b?.name || '';
  if (!aName && !bName) return 0;
  if (!aName) return 1;
  if (!bName) return -1;
  if (aName === keyword) return -1;
  if (bName === keyword) return 1;
  return aName?.localeCompare(bName);
};
/**
 * 按type聚合游戏数据
 * @param options 游戏列表数据
 * @returns 按type聚合后的级联游戏列表数据
 */
export const getCascaderOptions = (
  options: ITrendingGame[],
  cfg: { keyword: string },
): ITrendingGameEntityTypeData[] => {
  if (!Array.isArray(options)) return [];
  const { keyword } = cfg;
  const cascaderOptions: ITrendingGameEntityTypeData[] = [];

  // 聚合
  for (const option of options) {
    if (!option) continue;
    const { type } = option;
    // 特殊处理pc、console 为 combine
    const typeTmp = getCombileEntityType(type);
    let cascaderOption = cascaderOptions.find((item) => item?.type === typeTmp);
    if (!cascaderOption) {
      cascaderOption = {
        type: typeTmp,
        options: [],
      };
      cascaderOptions.push(cascaderOption);
    }
    cascaderOption.options.push(option);
  }

  /**
   * 子节点排序
   * name完全匹配keyword的节点排前面，然后按照name字符排序
   */
  cascaderOptions.forEach((cascaderOption) => {
    cascaderOption.options.sort((a, b) => {
      return optionsSorter(a, b, keyword);
    });
  });

  /**
   * 父节点排序，按照mobile、combile的顺序排
   */
  cascaderOptions.sort(cascaderOptionsSorter);

  return cascaderOptions;
};
